import React, { useEffect, useRef, useState } from 'react';
import { Alert, MenuItem, TextField } from '@mui/material';
import { useQuery } from 'react-query';
import { format, parseISO } from 'date-fns';
import { deaLogsApi, DeaSubstanceMatch } from '../../utils/api';

interface DeaBottleSelectProps {
  /** Drug name as typed on the dispense row — matched against the CS registry. */
  drugName: string;
  /** The row's declared DEA schedule ('II'…'V') — when set and the registry has
      no single match, surface that this dispense will NOT be auto-logged. */
  declaredSchedule?: string | null;
  /** Selected ControlledSubstanceLot.id ('' until the auto-preselect runs). */
  value: string;
  onChange: (deaLotId: string) => void;
  /**
   * Dispense quantity (in the substance's unit). Drives the auto-preselect:
   * the server's FIFO auto-pick is the first lot that can COVER the quantity,
   * not the first open lot — sufficiency is part of the UI↔ledger lockstep
   * (ADR-0016). Omitted/0 = treat any open bottle as coverable.
   */
  quantity?: number;
  disabled?: boolean;
  size?: 'small' | 'medium';
  margin?: 'none' | 'dense' | 'normal';
}

/**
 * "Bottle (lot #)" — the compliance prompt for dispensing DEA-logged drugs
 * (field vets may carry two bottles of the same substance on the truck).
 *
 * Mirrors DeductFromLocationSelect's fail-safe posture: renders NOTHING while
 * the registry match is unknown (loading / offline / error) or when the name
 * doesn't single-match the clinic's CS registry — the dispense then omits
 * `deaLotId` and the server's FIFO auto-pick applies, exactly as before.
 *
 * Auto-selection mirrors the server auto-pick — the first open bottle that can
 * COVER the quantity — and keeps tracking quantity edits until the user picks
 * a bottle themselves; an untouched control therefore always stamps the bottle
 * the server would have drawn, never one that would skip the entry. A named
 * bottle that can't cover (user-picked, or no bottle can) shows a warning: the
 * drug still dispenses, but the DEA entry will be skipped and flagged on the
 * unmatched-activity report.
 */
const DeaBottleSelect: React.FC<DeaBottleSelectProps> = ({
  drugName,
  declaredSchedule,
  value,
  onChange,
  quantity,
  disabled,
  size = 'small',
  margin = 'dense',
}) => {
  // Debounce the typed name so we don't hit the registry on every keystroke.
  const [debouncedName, setDebouncedName] = useState(drugName.trim());
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(drugName.trim()), 400);
    return () => clearTimeout(t);
  }, [drugName]);

  const { data: match } = useQuery<DeaSubstanceMatch>(
    ['dea-substance-match', debouncedName],
    () => deaLogsApi.matchSubstance(debouncedName),
    { enabled: debouncedName.length > 0, staleTime: 60 * 1000 },
  );

  const lots = match?.matched ? match.lots : [];
  const covers = (l: { quantityRemaining: string | number }) =>
    !quantity || Number(l.quantityRemaining) >= quantity;
  // Server auto-pick parity: first open lot with SUFFICIENT balance.
  const coveringLot = lots.find(covers);

  // An explicit user pick stops the auto-preselect from chasing quantity edits.
  const userTouched = useRef(false);

  useEffect(() => {
    if (!match?.matched || lots.length === 0) {
      // Name edit un-matched (or bottles disappeared) — clear a stale bottle.
      userTouched.current = false;
      if (value !== '') onChange('');
      return;
    }
    if (!lots.some((l) => l.id === value)) {
      // Selection belongs to a previous drug/lot list — reset to auto.
      userTouched.current = false;
    }
    if (!userTouched.current) {
      const target = coveringLot?.id ?? '';
      if (value !== target) onChange(target);
    }
  }, [match, value, quantity]); // lots/coveringLot derive from match+quantity

  if (!match || !match.matched) {
    // Dispense-time visibility (PR5): a row DECLARED controlled with no single
    // registry match means the CS auto-log will SKIP this dispense (INV-41's
    // ambiguity/no-match rule) — say so at the moment someone can fix it.
    if (match && declaredSchedule) {
      return (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {match.ambiguous
            ? `${drugName.trim()} matches more than one DEA registry entry — this dispense will NOT be auto-logged. Disambiguate it under Inventory → DEA Logs → Substances & Lots.`
            : `${drugName.trim()} is marked Schedule ${declaredSchedule} but isn't in your DEA registry — this dispense will NOT be logged to the DEA ledger. Add it under Inventory → DEA Logs → Substances & Lots.`}
        </Alert>
      );
    }
    return null;
  }

  const unit = match.substance?.unit ?? '';
  const name = match.substance?.genericName ?? drugName;

  if (lots.length === 0) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        {name} is in your DEA registry but has no open bottles — this dispense will be flagged for
        manual DEA entry. Add a lot in DEA Logs first to log it automatically.
      </Alert>
    );
  }

  const selected = lots.find((l) => l.id === value);
  const selectedInsufficient = !!selected && !covers(selected);
  const nothingCovers = !coveringLot;

  return (
    <>
      <TextField
        select
        label={`Bottle (lot #) — ${name}`}
        value={selected ? value : ''}
        onChange={(e) => {
          userTouched.current = true;
          onChange(e.target.value);
        }}
        disabled={disabled}
        size={size}
        margin={margin}
        fullWidth
        error={selectedInsufficient}
        helperText={
          selectedInsufficient
            ? `Only ${Number(selected!.quantityRemaining)} ${unit} left in this bottle — it can't cover this dispense, so the DEA entry will be skipped and flagged`
            : 'DEA-logged drug: confirm which bottle this came from'
        }
      >
        {lots.map((lot) => (
          <MenuItem key={lot.id} value={lot.id}>
            {lot.lotNumber} — exp {format(parseISO(lot.expirationDate), 'MMM yyyy')} —{' '}
            {Number(lot.quantityRemaining)} {unit} left
          </MenuItem>
        ))}
      </TextField>
      {nothingCovers && !selected && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          No open bottle of {name} can cover this quantity — the dispense will be flagged for
          manual DEA entry.
        </Alert>
      )}
    </>
  );
};

export default DeaBottleSelect;
